import React, { useState, useEffect, useRef } from 'react';
import { lightenColor, getTextColorForBackground } from '../utils/colorUtils';

const preselectedLocations = [
  { id: 1, name: "Cocina", icon: "🍴" },
  { id: 2, name: "Sala", icon: "🛋️" },
  { id: 3, name: "Baño", icon: "🚽" },
  { id: 4, name: "Habitación", icon: "🛏️" },
  { id: 5, name: "Comedor", icon: "🍽️" },
  { id: 6, name: "Balcón", icon: "🌇" },
];

const LocationSelector = ({ onLocationsChange, locations }) => {
  const [selectedLocations, setSelectedLocations] = useState(locations || []);
  const [newLocation, setNewLocation] = useState('');
  const [customLocations, setCustomLocations] = useState([]);
  const locationsContainerRef = useRef(null); // Ref para el contenedor de ubicaciones

  const handleToggleLocation = (location) => {
    setSelectedLocations(prev => {
      const isSelected = prev.some(l => l.id === location.id);
      const updatedLocations = isSelected
        ? prev.filter(l => l.id !== location.id)
        : [...prev, location];
      return updatedLocations;
    });
  };

  const handleAddLocation = () => {
    if (newLocation.trim() && !customLocations.some(l => l.name === newLocation.trim())) {
      const newId = customLocations.length + preselectedLocations.length + 1;
      const customLocation = { id: newId, name: newLocation.trim(), icon: "🏠", color: "#b5ead7" };
      setCustomLocations(prev => [...prev, customLocation]);
      setSelectedLocations(prev => [...prev, customLocation]);
      setNewLocation('');

      // Desplazar hacia el final cuando se agrega una nueva ubicación
      setTimeout(() => {
        locationsContainerRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  };

  const handleRemoveCustomLocation = (id) => {
    setCustomLocations(prev => prev.filter(l => l.id !== id));
    setSelectedLocations(prev => prev.filter(l => l.id !== id));
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleAddLocation();
    }
  };

  useEffect(() => {
    if (JSON.stringify(locations) !== JSON.stringify(selectedLocations)) {
      onLocationsChange(selectedLocations);
    }
  }, [selectedLocations, locations, onLocationsChange]);

  return (
    <div className="container p-4" style={{ paddingBottom: '60px', overflowY: 'auto' }}>
      <h2 className="text-2xl font-bold mb-4 text-center">Selecciona las Ubicaciones</h2>
      <div
        ref={locationsContainerRef} // Ref asignado al contenedor de ubicaciones
        className="grid grid-cols-2 gap-4 mb-0"
      >
        {preselectedLocations.map((location) => (
          <button
            key={location.id}
            onClick={() => handleToggleLocation(location)}
            className={`p-4 rounded-lg border flex flex-col items-center justify-center ${selectedLocations.some(l => l.id === location.id) ? 'bg-primary-color text-white' : 'bg-gray-200 text-gray-800'}`}
          >
            <span className="text-4xl">{location.icon}</span>
            <span>{location.name}</span>
          </button>
        ))}
      </div>

      <div className="flex flex-wrap mt-4">
        {customLocations.map((location) => {
          const backgroundColor = lightenColor(location.color, 40);
          const textColor = getTextColorForBackground(location.color);

          return (
            <div key={location.id} className="inline-flex items-center rounded-full px-3 py-1 text-sm bg-primary-color"
              style={{ backgroundColor: backgroundColor, color: textColor }}>
              <span className="text-2xl mr-2">{location.icon}</span>
              <span className="mr-2">{location.name}</span>
              <span
                onClick={() => handleRemoveCustomLocation(location.id)}
                className="ml-2 cursor-pointer"
                style={{ color: textColor }}
              >
                &#10005;
              </span>
            </div>
          );
        })}
      </div>

      <div className="flex flex-row mt-4 gap-2">
        <input
          type="text"
          placeholder="Nueva ubicación"
          value={newLocation}
          onChange={(e) => setNewLocation(e.target.value)}
          onKeyPress={handleKeyPress}
          className="p-4 border border-gray-300 rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-primary-color"
        />
        <button
          onClick={handleAddLocation}
          className="opacity-70 flex items-center justify-center p-4 rounded-lg shadow-lg text-center w-1/3"
        >
          <span className="text-xl font-bold">añadir</span>
        </button>
      </div>
      <p className="text-xs text-center text-gray-600">Elige al menos una ubicación para poder continuar</p>

    </div>
  );
};

export default LocationSelector;
